import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Upload, FileText, Eye, X } from 'lucide-react';
import DocumentUpload from '@/components/Posts/DocumentUpload';
import MarkdownRenderer from '@/components/Posts/MarkdownRenderer';
import ImageSelector from '@/components/Posts/ImageSelector';
import LocationCloner from '@/components/Posts/LocationCloner';
import BulkLocationCloner from '@/components/Posts/BulkLocationCloner';
import PostPreview from '@/components/Posts/PostPreview';

interface Variable {
  id: string;
  name: string;
  value: string;
  syntax: string;
}

interface ServiceLocation {
  id: string;
  name: string;
  county: string;
  population: number;
  coordinates: string;
  latitude: number;
  longitude: number;
  historical_fact: string;
  key_search_terms: string;
}

const CreatePost = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('edit');
  const isEditing = !!editId;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [textInput, setTextInput] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  
  // SEO Fields
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  
  // Categories and Tags
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Fetch variables for preview
  const { data: variables = [] } = useQuery({
    queryKey: ['contentVariables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_variables')
        .select('id, name, value, syntax')
        .limit(10);
      
      if (error) throw error;
      return data as Variable[];
    },
  });

  // Fetch service locations for location-specific variables
  const { data: serviceLocations = [] } = useQuery({
    queryKey: ['serviceLocations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_locations')
        .select('*')
        .limit(1); // Get first location for preview
      
      if (error) throw error;
      return data as ServiceLocation[];
    },
  });

  // Fetch existing post data when editing
  const { data: existingPost, isLoading: isLoadingPost } = useQuery({
    queryKey: ['blogPost', editId],
    queryFn: async () => {
      if (!editId) return null;
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', editId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!editId,
  });

  // Load existing post data into form when editing
  useEffect(() => {
    if (existingPost && isEditing) {
      setTitle(existingPost.title || '');
      setContent(existingPost.content || '');
      setAuthor(existingPost.author || '');
      setExcerpt(existingPost.excerpt || '');
      setFeaturedImage(existingPost.featured_image || '');
      setSeoTitle(existingPost.seo_title || '');
      setSeoDescription(existingPost.seo_description || '');
      setSeoKeywords(existingPost.seo_keywords || []);
      setCategories(existingPost.categories || []);
      setTags(existingPost.tags || []);
    }
  }, [existingPost, isEditing]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setTextInput(text);
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
      toast.success('File content loaded successfully');
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read file');
    }
  };

  const handleDocumentUpload = (extractedContent: string, extractedTitle?: string) => {
    setContent(extractedContent);
    if (extractedTitle) {
      setTitle(extractedTitle);
    }
    toast.success('Document content loaded into editor');
  };

  const handleUseTextInput = () => {
    setContent(textInput);
    toast.success('Text input moved to content editor');
  };

  // Tag and Category Management
  const addKeyword = () => {
    if (keywordInput.trim() && !seoKeywords.includes(keywordInput.trim())) {
      setSeoKeywords([...seoKeywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setSeoKeywords(seoKeywords.filter(k => k !== keyword));
  };

  const addCategory = () => {
    if (categoryInput.trim() && !categories.includes(categoryInput.trim())) {
      setCategories([...categories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const removeCategory = (category: string) => {
    setCategories(categories.filter(c => c !== category));
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const replaceVariables = (text: string): string => {
    let replacedText = text;
    
    // Replace content variables from database
    variables.forEach(variable => {
      const regex = new RegExp(`\\${variable.syntax.replace('{', '\\{').replace('}', '\\}')}`, 'g');
      replacedText = replacedText.replace(regex, variable.value);
    });

    // Map article variables to our database structure using first location
    const location = serviceLocations[0];
    if (location) {
      // Map location-specific variables
      const locationMappings = {
        '{name}': location.name,
        '{locationName}': location.name,
        '{county}': location.county,
        '{locationCounty}': location.county,
        '{coordinates}': location.coordinates,
        '{historical_fact}': location.historical_fact || 'This historic community has a rich heritage dating back generations.',
        '{locationDescription}': location.historical_fact || `the ${location.name} area`,
        '{key_search_terms}': location.key_search_terms || `${location.name} area`,
        '{locationKeyTerms}': location.key_search_terms || `${location.name} roofing, ${location.county} roofing services`,
        '{locationPopulation}': location.population?.toString() || 'local community',
        '{population}': location.population?.toString() || 'local community',
        '{locationClimate}': getLocationClimate(location.name)
      };

      Object.entries(locationMappings).forEach(([placeholder, value]) => {
        const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
        replacedText = replacedText.replace(regex, value);
      });

      // Add location-specific phone number - when {name} is used in context of office
      const locationPhoneRegex = new RegExp(`${location.name} office`, 'gi');
      if (locationPhoneRegex.test(replacedText)) {
        replacedText = replacedText.replace(/{locationPhone}/g, '(252) 371-2266');
      }
    }

    // Add business information with correct details
    const businessMappings = {
      '{businessPhone}': '(252) 302-2003',
      '{businessEmail}': 'claims@carolinaTrophyRoofs.com',
      '{businessAddress}': '104 S. Business Ct., Rocky Mount, NC 27804',
      '{businessName}': 'Carolina Trophy Roofs',
      '{businessWebsite}': 'CarolinaTrophyRoofs.com',
      '{locationPhone}': '(252) 371-2266'
    };

    Object.entries(businessMappings).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
      replacedText = replacedText.replace(regex, value);
    });

    // Add current date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    replacedText = replacedText.replace(/{current_date}/g, currentDate);

    return replacedText;
  };

  const getLocationClimate = (name: string) => {
    if (name.toLowerCase().includes('florida') || name.toLowerCase().includes('miami')) {
      return 'humid subtropical climate with heavy rainfall and hurricane risks';
    }
    if (name.toLowerCase().includes('texas') || name.toLowerCase().includes('austin')) {
      return 'hot summers and mild winters with occasional severe weather';
    }
    if (name.toLowerCase().includes('california') || name.toLowerCase().includes('san')) {
      return 'Mediterranean climate with dry summers and mild, wet winters';
    }
    return 'varied seasonal weather patterns requiring weather-resistant roofing';
  };

  const previewContent = replaceVariables(content);
  const previewTitle = replaceVariables(title);
  const previewAuthor = replaceVariables(author);
  const previewExcerpt = replaceVariables(excerpt);
  const previewSeoTitle = replaceVariables(seoTitle);
  const previewSeoDescription = replaceVariables(seoDescription);
  
  // Apply variable replacement to keywords, categories, and tags
  const previewSeoKeywords = seoKeywords.map(keyword => replaceVariables(keyword));
  const previewCategories = categories.map(category => replaceVariables(category));
  const previewTags = tags.map(tag => replaceVariables(tag));

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      const slug = title.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const postData = {
        title: title.trim(),
        content: content.trim(),
        excerpt: excerpt.trim() || null,
        slug,
        author: author.trim() || 'Admin',
        status: 'draft',
        featured_image: featuredImage || null,
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_keywords: seoKeywords.length > 0 ? seoKeywords : null,
        categories: categories.length > 0 ? categories : null,
        tags: tags.length > 0 ? tags : null
      };

      if (isEditing) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editId);

        if (error) throw error;
        toast.success('Blog post updated successfully');
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert(postData);

        if (error) throw error;
        toast.success('Blog post saved as draft');
        
        // Reset form for new posts
        setTitle('');
        setContent('');
        setAuthor('');
        setExcerpt('');
        setTextInput('');
        setFeaturedImage('');
        setSeoTitle('');
        setSeoDescription('');
        setSeoKeywords([]);
        setCategories([]);
        setTags([]);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save blog post');
    }
  };

  const handleCloneComplete = () => {
    toast.success('Posts cloned successfully! Redirecting to posts page...');
    setTimeout(() => {
      navigate('/posts');
    }, 2000);
  };

  if (isLoadingPost) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditing 
              ? 'Update your blog post and clone to service locations' 
              : 'Upload files, paste content, and preview with variables'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input */}
          <div className="space-y-6">
            {/* Show LocationCloner when editing */}
            {isEditing && existingPost && (
              <LocationCloner
                blogPost={{
                  title,
                  content,
                  excerpt,
                  author,
                  categories,
                  tags,
                  seoTitle,
                  seoDescription,
                  featuredImage,
                }}
                onCloneComplete={handleCloneComplete}
              />
            )}

            {/* Bulk Location Cloner - show when NOT editing (for admins) */}
            {!isEditing && (
              <BulkLocationCloner />
            )}

            {/* File Upload Section - only show when creating new post */}
            {!isEditing && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    File Upload
                  </CardTitle>
                  <CardDescription>
                    Upload a text file to import content
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="file-upload">Select File (.txt, .md)</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".txt,.md"
                      onChange={handleFileUpload}
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Document Upload Component - only show when creating new post */}
            {!isEditing && (
              <DocumentUpload onContentExtracted={handleDocumentUpload} />
            )}

            {/* Text Input Section - only show when creating new post */}
            {!isEditing && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Paste Text
                  </CardTitle>
                  <CardDescription>
                    Paste or type content directly
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Paste your content here..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="min-h-32"
                  />
                  <Button 
                    onClick={handleUseTextInput}
                    disabled={!textInput.trim()}
                    variant="outline"
                    className="w-full"
                  >
                    Use This Text
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Featured Image Selection */}
            <ImageSelector
              selectedImage={featuredImage}
              onImageSelect={setFeaturedImage}
              blogTags={tags}
              blogCategories={categories}
            />

            {/* Combined Blog Post & SEO Section */}
            <Card>
              <CardHeader>
                <CardTitle>Blog Post Details & SEO</CardTitle>
                <CardDescription>
                  Configure your post content and optimize for search engines
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter blog post title"
                  />
                </div>

                <div>
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter your blog post content here. Use variables like {businessName}, {businessPhone}, {locationPhone}, {name}, {county}, {coordinates}, etc."
                    className="min-h-48"
                  />
                </div>

                <div>
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Enter author name"
                  />
                </div>

                <div>
                  <Label htmlFor="excerpt">Excerpt (Optional)</Label>
                  <Textarea
                    id="excerpt"
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    placeholder="Brief description of the post"
                    className="min-h-20"
                  />
                </div>

                <div>
                  <Label htmlFor="seo-title">SEO Title (Optional)</Label>
                  <Input
                    id="seo-title"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder="Custom SEO title (defaults to post title)"
                  />
                </div>

                <div>
                  <Label htmlFor="seo-description">Meta Description</Label>
                  <Textarea
                    id="seo-description"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    placeholder="Brief description for search engines (150-160 characters)"
                    className="min-h-20"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {seoDescription.length}/160 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="seo-keywords">SEO Keywords</Label>
                  <div className="flex gap-2">
                    <Input
                      id="seo-keywords"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      placeholder="Enter keyword and press Enter"
                      onKeyPress={(e) => handleKeyPress(e, addKeyword)}
                    />
                    <Button onClick={addKeyword} variant="outline" size="sm">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {seoKeywords.map((keyword) => (
                      <Badge key={keyword} variant="secondary" className="text-sm">
                        {keyword}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer" 
                          onClick={() => removeKeyword(keyword)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="categories">Categories</Label>
                  <div className="flex gap-2">
                    <Input
                      id="categories"
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      placeholder="Enter category and press Enter"
                      onKeyPress={(e) => handleKeyPress(e, addCategory)}
                    />
                    <Button onClick={addCategory} variant="outline" size="sm">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {categories.map((category) => (
                      <Badge key={category} variant="default" className="text-sm">
                        {category}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer" 
                          onClick={() => removeCategory(category)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="tags">Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      id="tags"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Enter tag and press Enter"
                      onKeyPress={(e) => handleKeyPress(e, addTag)}
                    />
                    <Button onClick={addTag} variant="outline" size="sm">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-sm">
                        #{tag}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer" 
                          onClick={() => removeTag(tag)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSave} className="w-full">
                  {isEditing ? 'Update Post' : 'Save as Draft'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Live Preview
                </CardTitle>
                <CardDescription>
                  Preview with variables substituted and HTML/Markdown rendered
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="preview" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                    <TabsTrigger value="variables">Variables</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="preview" className="mt-4">
                    <div className="prose prose-sm max-w-none">
                      {featuredImage && (
                        <div className="mb-4">
                          <img
                            src={featuredImage}
                            alt="Featured image"
                            className="w-full h-48 object-cover rounded-md"
                          />
                        </div>
                      )}
                      {title && (
                        <h1 className="text-2xl font-bold mb-4">
                          {previewTitle}
                        </h1>
                      )}
                      {author && (
                        <p className="text-sm text-muted-foreground mb-4">
                          By {previewAuthor}
                        </p>
                      )}
                      
                      {/* SEO Preview Section */}
                      {(seoTitle || seoDescription || seoKeywords.length > 0) && (
                        <div className="mb-4 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                          <h4 className="font-semibold text-sm mb-2 text-blue-800">SEO Preview:</h4>
                          {seoTitle && (
                            <p className="text-sm"><strong>SEO Title:</strong> {previewSeoTitle}</p>
                          )}
                          {seoDescription && (
                            <p className="text-sm"><strong>Meta Description:</strong> {previewSeoDescription}</p>
                          )}
                          {seoKeywords.length > 0 && (
                            <div className="text-sm">
                              <strong>Keywords:</strong>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {previewSeoKeywords.map((keyword, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {keyword}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {excerpt && (
                        <div className="text-sm italic mb-4 p-3 bg-muted rounded">
                          {previewExcerpt}
                        </div>
                      )}
                      {(categories.length > 0 || tags.length > 0) && (
                        <div className="mb-4 flex flex-wrap gap-2">
                          {previewCategories.map((category, index) => (
                            <Badge key={index} variant="default">
                              {category}
                            </Badge>
                          ))}
                          {previewTags.map((tag, index) => (
                            <Badge key={index} variant="outline">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {previewContent ? (
                        <MarkdownRenderer content={previewContent} />
                      ) : (
                        <p className="text-muted-foreground italic">
                          Content will appear here as you type...
                        </p>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="variables" className="mt-4">
                    <div className="space-y-3">
                      <h3 className="font-semibold">Available Variables:</h3>
                      
                      {/* Business Variables */}
                      <div>
                        <h4 className="font-medium text-sm mb-2">Business Variables:</h4>
                        <div className="space-y-2">
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{businessName}`}</div>
                            <div className="text-muted-foreground">→ Carolina Trophy Roofs</div>
                          </div>
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{businessPhone}`}</div>
                            <div className="text-muted-foreground">→ (252) 302-2003</div>
                          </div>
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{businessEmail}`}</div>
                            <div className="text-muted-foreground">→ claims@carolinaTrophyRoofs.com</div>
                          </div>
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{businessAddress}`}</div>
                            <div className="text-muted-foreground">→ 104 S. Business Ct., Rocky Mount, NC 27804</div>
                          </div>
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{businessWebsite}`}</div>
                            <div className="text-muted-foreground">→ CarolinaTrophyRoofs.com</div>
                          </div>
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{locationPhone}`}</div>
                            <div className="text-muted-foreground">→ (252) 371-2266 (for location-specific offices)</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content Variables */}
                      <div>
                        <h4 className="font-medium text-sm mb-2">Content Variables:</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {variables.map((variable) => (
                            <div key={variable.id} className="p-2 border rounded text-sm">
                              <div className="font-mono text-blue-600">
                                {variable.syntax}
                              </div>
                              <div className="text-muted-foreground">
                                → {variable.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Location Variables */}
                      <div>
                        <h4 className="font-medium text-sm mb-2">Location Variables:</h4>
                        <div className="space-y-2">
                          {serviceLocations.length > 0 ? (
                            <>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{name}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].name}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationName}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].name}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{county}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].county}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationCounty}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].county}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{coordinates}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].coordinates}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{historical_fact}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].historical_fact || 'Historic community heritage'}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationDescription}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].historical_fact || `the ${serviceLocations[0].name} area`}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{key_search_terms}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].key_search_terms || `${serviceLocations[0].name} area`}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationKeyTerms}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].key_search_terms || `${serviceLocations[0].name} roofing, ${serviceLocations[0].county} roofing services`}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationPopulation}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].population?.toString() || 'local community'}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{population}`}</div>
                                <div className="text-muted-foreground">→ {serviceLocations[0].population?.toString() || 'local community'}</div>
                              </div>
                              <div className="p-2 border rounded text-sm">
                                <div className="font-mono text-green-600">{`{locationClimate}`}</div>
                                <div className="text-muted-foreground">→ {getLocationClimate(serviceLocations[0].name)}</div>
                              </div>
                            </>
                          ) : (
                            <p className="text-muted-foreground text-sm">No locations found</p>
                          )}
                          <div className="p-2 border rounded text-sm">
                            <div className="font-mono text-purple-600">{`{current_date}`}</div>
                            <div className="text-muted-foreground">→ {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                          </div>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground p-3 bg-blue-50 rounded-md">
                        <p className="font-medium mb-1">💡 Usage Examples:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Use <code>{`{locationPhone}`}</code> when mentioning "office" after a city name</li>
                          <li>Example: "Contact our {`{name}`} office at {`{locationPhone}`}" becomes "Contact our Raleigh office at (252) 371-2266"</li>
                          <li>Use <code>{`{businessPhone}`}</code> for general contact information</li>
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreatePost;
